/**
 * Computed generator version: a fingerprint of what the generator actually
 * produces, not a hand-bumped label. Canonical placements of fixed probe
 * seeds (decor included, against a synthetic catalog that exercises every
 * setting rule pattern) are hashed with cyrb128 — pure 32-bit integer ops,
 * bit-identical across JS engines. Any behavior change anywhere in the
 * pipeline changes the version; refactors that preserve output don't.
 *
 * Shown in the HUD and embedded in inspect-mode debug reports, so a version
 * mismatch between a report and a fresh generate means a stale bundle.
 */
import { generateTrack } from "./generateTrack.ts";
import { cyrb128 } from "./rng.ts";

const PROBE_SEEDS = [
  "version-probe-a",
  "version-probe-b",
  "version-probe-c",
  "version-probe-d",
];

/** Fake but pattern-covering catalog: decor logic runs deterministically
 * without needing the real manifest at version-compute time. */
const PROBE_CATALOG = {
  packs: [{
    slug: "nature-kit",
    ids: [
      "tree_a",
      "tree_dead",
      "rock_a",
      "stone_b",
      "grass_a",
      "plant_a",
      "flower_a",
      "mushroom_a",
      "stump_a",
      "log_a",
      "cliff_a",
      "fence_a",
      "crop_a",
    ],
  }],
};

let cached: string | null = null;

/** ~40ms on first call (4 full generations), cached afterwards. */
export function genVersion(): string {
  if (cached) return cached;
  const parts: string[] = [];
  for (const seed of PROBE_SEEDS) {
    const t = generateTrack(seed, PROBE_CATALOG);
    for (const p of t.placements) {
      parts.push(
        [
          p.pieceId,
          p.pos.map((v) => Math.round(v * 4)).join(","),
          p.rotation,
          p.mirrored ? 1 : 0,
          p.role,
          p.scale !== undefined ? Math.round(p.scale * 16) : "",
          p.spin ? Math.round(p.spin.radPerSec * 16) : "",
        ].join("|"),
      );
    }
    parts.push(`;${seed}`);
  }
  const [a, b] = cyrb128(parts.join("\n"));
  cached = "g" + (a >>> 0).toString(16).padStart(8, "0") +
    ((b >>> 0).toString(16).padStart(8, "0")).slice(0, 4);
  return cached;
}
